// The artifact download endpoint (GET /api/v1/artifacts/{artifact_id}/download)
// requires the same bearer token the shared API client attaches in dev (auth
// is cookie-based in prod, see shared/auth/tokenStorage.ts) — a plain
// `<img src>` can't set that header, so fetch the bytes with
// artifactFetchInit's credentials and hand the browser a blob URL instead. A
// failed fetch reports `failed` so callers can fall back to a filename chip
// rather than a spinner that never resolves.

import { useEffect, useState } from 'react';
import { artifactFetchInit } from './artifactFetch';

export type AuthenticatedObjectUrl = { src: string | null; failed: boolean };

const PENDING: AuthenticatedObjectUrl = { src: null, failed: false };

export function useAuthenticatedObjectUrl(url: string | undefined): AuthenticatedObjectUrl {
  const [state, setState] = useState<AuthenticatedObjectUrl>(PENDING);

  useEffect(() => {
    setState(PENDING);
    if (!url) return undefined;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    (async () => {
      try {
        const response = await fetch(url, { ...artifactFetchInit(url), signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!response.ok) {
          setState({ src: null, failed: true });
          return;
        }
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ src: objectUrl, failed: false });
      } catch {
        if (!controller.signal.aborted) setState({ src: null, failed: true });
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return state;
}
